import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import { Region } from '@enonic/react-components';

import RichText from '../../../react4xp/components/RichText'
import Image from '../../../react4xp/components/Image'
import Section from '../../../react4xp/components/Section'
import ProductCardBarcode from '../../../react4xp/components/ProductCardBarcode'
import Form from '../../../react4xp/components/Form/Form'
import Input from '../../../react4xp/components/Form/Input'
import Button from '../../../react4xp/components/Button'
import Fancybox from '../../../react4xp/components/Fancybox'
import MasonryGrid from '../../../react4xp/components/MasonryGrid'
import TextArea from '../../../react4xp/components/Form/TextArea'
import Link from '../../../react4xp/components/Link'
import Stars from '../../../react4xp/components/Stars'

import { GeneralClose, GeneralStar } from '../../../react4xp/components/Svg/Icons/General'
import { EmojiHappy } from '../../../react4xp/components/Svg/Icons/Emoji'
import { UxAngleDown } from '../../../react4xp/components/Svg/Ux/Angle'

import dayjs from 'dayjs'
import 'dayjs/locale/nb'

function ProductLayout({ colors, title, intro, image, sectionStores, additionalPackaging, backgroundImage, isLogged, allergens, tracesOf, strings, ingredients, nutritionFacts, totalReviews, rateProductUrl, productId, profilePageUrl, commentProductUrl, pagination, rating, regions }) {

    // // reviews
    const [reviews, setReviews] = useState<any[]>([])
    const [page, setPage] = useState(pagination.page || 1)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [loadMore, setLoadMore] = useState(false)
    const [ratingValue, setRatingValue] = useState(rating)

    useEffect(() => {
        handleFetchComments()
    }, [pagination.pageSize, page])

    const handleFetchComments = async () => {

        if (!pagination.endpoint) return;

        const origin = window.location.origin;
        const url = new URL(pagination.endpoint);
        const queryParams = new URLSearchParams();

        if (page) queryParams.append('page', String(page));
        if (pagination.pageSize) queryParams.append('pageSize', pagination.pageSize);
        queryParams.append('contentId', productId);

        url.search = queryParams.toString();

        // updateCurrentUrl()

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`);
            }
            const data = await response.json();

            if (loadMore) {
                setReviews([...reviews, ...data.hits]);
                setLoadMore(false)
            } else {
                setReviews(data.hits);
            }

            setHasNextPage(data.hasNextPage);

        } catch (error) {
            console.error('There was a problem with the fetch operation:', error);
        }
    }

    const handleLoadMoreReviews = () => {
        if (hasNextPage) {
            setLoadMore(true)
            setPage(page + 1)
        }
    }

    // const displayNumber = 4

    // const [displayedReviews, setDisplayedReviews] = useState(displayNumber)

    // const loadMoreReviews = () => {
    //     setDisplayedReviews(prev => prev + displayNumber)
    // }

    // accordion effects on related products
    const [isProductsAccordionActive, setIsProductsAccordionActive] = useState(false)
    const toggleProductsAccordion = () => { setIsProductsAccordionActive((prev) => !prev) }

    // toggle all details blocks
    const [isNutritionalFactsActive, setIsNutritionalFactsActive] = useState(false)
    const toggleNutritionalFacts = () => { setIsNutritionalFactsActive((prev) => !prev) }

    const [isIngredientsActive, setIsIngredientsActive] = useState(false)
    const toggleIngredients = () => { setIsIngredientsActive((prev) => !prev) }

    const [isAllergensActive, setIsAllergensActive] = useState(false)
    const toggleAllergens = () => { setIsAllergensActive((prev) => !prev) }

    const [isShopsActive, setIsShopsActive] = useState(false)
    const toggleShops = () => { setIsShopsActive((prev) => !prev) }

    const [lastWidth, setLastWidth]: any = useState(null)

    const [totalReviewsState, setTotalReviewsState] = useState(totalReviews)

    useEffect(() => {
        const checkWindowWidth = () => {
            const currentWidth = window.innerWidth

            if (currentWidth !== lastWidth) {
                setLastWidth(currentWidth)

                const isWideScreen = currentWidth >= 768
                setIsNutritionalFactsActive(isWideScreen)
                setIsIngredientsActive(isWideScreen)
                setIsAllergensActive(isWideScreen)
                setIsShopsActive(isWideScreen)
            }
        }

        checkWindowWidth()

        window.addEventListener('resize', checkWindowWidth)

        return () => {
            window.removeEventListener('resize', checkWindowWidth)
        }
    }, [lastWidth])

    const [fancyEventInit, setFancyEventInit] = useState(false)

    // update tabindex based on the window width
	const [tabIndex, setTabIndex]: any = useState(null)

	useEffect(() => {
		const updateTabIndex = () => {
			if (window.innerWidth >= 768) {
				setTabIndex(-1)
			} else {
				setTabIndex(null)
			}
		}

		updateTabIndex()

		window.addEventListener('resize', updateTabIndex)

		return () => window.removeEventListener('resize', updateTabIndex)
	}, [])

    return (
        <>
            <Section
                className='page-product__header'
                bgColor={colors.bgColor}
                paddingTop='none'
                paddingBottom='none'
            >
                <div className='container'>
                    <div className='row'>

                        <div className={clsx('col-lg-6 page-product__header__texts-col', colors.textColor)}>

                            <h1 className='h1'>
                                {title}
                            </h1>

                            <RichText
                                className='text-big'
                                content={intro}
                            />

                        </div>

                        {image?.link && (
                            <div className='col-lg-6 page-product__header__image-col'>
                                <Image
                                    src={image?.link}
                                    srcMedium={image?.link}
                                    alt={image?.alt}
                                    width={800}
                                    sizes='( max-width: 992px ) 100vw, 800px'
                                />
                            </div>
                        )}

                    </div>
                </div>
            </Section>

            {
                backgroundImage.large &&
                <section className='page-product__top-image'>
                    <Image
                        src={backgroundImage.large?.link} // 1440 x 520
                        srcMedium={backgroundImage.medium?.link} // 990 x 360
                        srcSmall={backgroundImage.small?.link} // 560 x 300
                        alt={backgroundImage.large?.alt}
                        className='cover'
                    />
                </section>
            }

            <Region name={regions.main.name} regionData={regions.main} />

            <Section
                bgColor='white'
                className='page-product__details'
                //paddingTop='big'
                paddingBottom='small'
            >
                <div className='container'>

                    <div className='page-product__details__top'>
                        <div className='row'>
                            {
                                (nutritionFacts.length > 0) &&
                                <div className='col-md-6 left'>
                                    <div className='block' style={{ backgroundColor: colors.bgColor }}>
                                        <div className={clsx(
                                            'accordion',
                                            isNutritionalFactsActive && 'is-active',
                                            colors.textColor
                                        )}>

                                            <button
                                                type='button'
                                                className='accordion__title'
                                                onClick={toggleNutritionalFacts}
                                                tabIndex={tabIndex}
                                            >

                                                <h3 className='products-box__title h4'>
                                                    {nutritionFacts[0].title}
                                                </h3>

                                                <UxAngleDown />

                                            </button>

                                            <div className='accordion__content'>
                                                <div>

                                                    <ul>

                                                        {
                                                            nutritionFacts[0].rows.map((row, i) => {

                                                                const isSubItem = row?.text?.toLowerCase().trim() === 'hvorav mettede fettsyrer' || row?.text?.toLowerCase().trim() === 'hvorav sukkerarter'

                                                                return (
                                                                    <li key={i} className={clsx(isSubItem && 'accordion__content__italic')}>
                                                                        <p>{row.text}</p>
                                                                        <p>{row.value}</p>
                                                                    </li>
                                                                )
                                                            })
                                                        }

                                                        {
                                                            nutritionFacts[0].footnote &&
                                                            <li key={'0-footnote'} className='full-line'>

                                                                <p>{nutritionFacts[0].footnote}</p>

                                                            </li>
                                                        }

                                                    </ul>

                                                    {
                                                        nutritionFacts.slice(1).map((nutritionFact, i) =>
                                                            <>
                                                                <h3 key={i} className='h4'>{nutritionFact.title}</h3>
                                                                {
                                                                    (nutritionFact.rows.length > 0) &&
                                                                    <ul>
                                                                        {
                                                                            nutritionFact.rows.map((row, j) => {
                                                                                
                                                                                const isSubItem = row?.text?.toLowerCase().trim() === 'hvorav mettede fettsyrer' || row?.text?.toLowerCase().trim() === 'hvorav sukkerarter'

                                                                                return (
                                                                                    <li key={j} className={clsx(isSubItem && 'accordion__content__italic')}>
                                                                                        <p>{row.text}</p>
                                                                                        <p>{row.value}</p>
                                                                                    </li>
                                                                                )
                                                                            })
                                                                        }

                                                                        {
                                                                            nutritionFact.footnote &&
                                                                            <li key={`${i}-footnote`} className='full-line'>

                                                                                <p>{nutritionFact.footnote}</p>

                                                                            </li>
                                                                        }
                                                                    </ul>
                                                                }
                                                            </>
                                                        )
                                                    }

                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            }

                            <div className='col-md-6 right'>
                                {
                                    ingredients &&
                                    <div className='block'>
                                        <div className={clsx(
                                            'accordion',
                                            isIngredientsActive && 'is-active'
                                        )}>

                                            <button
                                                type='button'
                                                className='accordion__title'
                                                onClick={toggleIngredients}
                                                tabIndex={tabIndex}
                                            >

                                                <h3 className='products-box__title h4'>
                                                    {strings.ingredientsHeader}
                                                </h3>

                                                <UxAngleDown />

                                            </button>

                                            <div className='accordion__content'>
                                                <div dangerouslySetInnerHTML={{ __html: ingredients }} suppressHydrationWarning></div>
                                            </div>

                                        </div>
                                    </div>
                                }

                                {
                                    ((allergens.length > 0) || (tracesOf.length > 0)) &&
                                    <div className='block expand'>
                                        <div className={clsx(
                                            'accordion',
                                            isAllergensActive && 'is-active'
                                        )}>

                                            <button
                                                type='button'
                                                className='accordion__title'
                                                onClick={toggleAllergens}
                                                tabIndex={tabIndex}
                                            >

                                                <h3 className='products-box__title h4'>
                                                    {strings.allergensHeader}
                                                </h3>

                                                <UxAngleDown />

                                            </button>

                                            <div className='accordion__content'>
                                                <div>
                                                    {
                                                        (allergens.length > 0) &&
                                                        <div className='tags text-small'>
                                                            {
                                                                allergens.map((allergen, i) =>
                                                                    <div className='tag bg-white' key={i}>
                                                                        {allergen.icon && <span dangerouslySetInnerHTML={{ __html: allergen.icon }}></span>}
                                                                        <p>{allergen.title}</p>
                                                                    </div>
                                                                )
                                                            }
                                                        </div>
                                                    }

                                                    {
                                                        (tracesOf.length > 0) &&
                                                        <>
                                                            <h3 className='h4'>
                                                                {strings.tracesOfHeader}
                                                            </h3>
                                                        
                                                            <div className='tags text-small'>
                                                                {
                                                                    tracesOf.map((allergen, i) =>
                                                                        <div className='tag bg-white' key={i}>
                                                                            {allergen.icon && <span dangerouslySetInnerHTML={{ __html: allergen.icon }}></span>}
                                                                            <p>{allergen.title}</p>
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>
                                                        </>
                                                    }

                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                }

                            </div>

                        </div>
                    </div>

                    {
                        (sectionStores.length > 0) &&
                        <div className='page-product__details__bottom'>
                            <div className='block'>
                                <div className={clsx(
                                    'accordion',
                                    isShopsActive && 'is-active'
                                )}>

                                    <button
                                        type='button'
                                        className='accordion__title'
                                        onClick={toggleShops}
                                        tabIndex={tabIndex}
                                    >

                                        <h3 className='products-box__title h4'>
                                            {strings.shopsHeader}
                                        </h3>

                                        <UxAngleDown />

                                    </button>

                                    <div className='accordion__content'>
                                        <div>

                                            {sectionStores.length === 1 && (
                                                <div className='grid grid--1-col'>

                                                    {
                                                        (sectionStores[0].storeAt.length > 0) &&
                                                        <div className='flex-inner'>

                                                            <p>
                                                                {strings.storeAtHeader}
                                                            </p>

                                                            <div className='tags text-small'>
                                                                {
                                                                    sectionStores[0].storeAt.map((label, i) =>
                                                                        <div key={i} className='tag bg-white'>
                                                                            <p>
                                                                                {label}
                                                                            </p>
                                                                        </div>
                                                                    )
                                                                }

                                                            </div>

                                                        </div>
                                                    }

                                                    {
                                                        (sectionStores[0].productAt.length > 0) &&
                                                        <div className='flex-inner'>

                                                            <p>
                                                                {strings.productAtHeader}
                                                            </p>

                                                            <div className='tags text-small'>

                                                                {sectionStores[0].productAt.map((label, i) => (
                                                                    <div className='tag bg-white' key={i}>
                                                                        <p>
                                                                            {label}
                                                                        </p>
                                                                    </div>
                                                                ))}

                                                            </div>

                                                        </div>
                                                    }


                                                </div>
                                            )}

                                            {sectionStores.length === 2 && (
                                                <div className='grid grid--2-cols'>
                                                    {
                                                        sectionStores.map((section, i) =>
                                                            <div key={i} className='flex-inner'>

                                                                <p>
                                                                    <b>{section.title}</b>
                                                                </p>

                                                                {
                                                                    (section.storeAt.length > 0) &&
                                                                    <>
                                                                        <p>
                                                                            {strings.storeAtHeader}
                                                                        </p>

                                                                        <div className='tags text-small'>
                                                                            {
                                                                                section.storeAt.map((label, i) =>
                                                                                    <div key={i} className='tag bg-white'>
                                                                                        <p>
                                                                                            {label}
                                                                                        </p>
                                                                                    </div>
                                                                                )
                                                                            }

                                                                        </div>
                                                                    </>
                                                                }

                                                                {
                                                                    (section.productAt.length > 0) &&
                                                                    <>
                                                                        <p>
                                                                            {strings.productAtHeader}
                                                                        </p>

                                                                        <div className='tags text-small'>

                                                                            {section.productAt.map((label, i) => (
                                                                                <div className='tag bg-white' key={i}>
                                                                                    <p>
                                                                                        {label}
                                                                                    </p>
                                                                                </div>
                                                                            ))}

                                                                        </div>
                                                                    </>
                                                                }

                                                            </div>
                                                        )
                                                    }

                                                </div>
                                            )}

                                            {sectionStores.length === 3 && (
                                                <div className='grid grid--3-cols'>
                                                    {
                                                        sectionStores.map((section, i) =>
                                                            <div key={i} className='flex-inner'>

                                                                <p>
                                                                    <b>{section.title}</b>
                                                                </p>

                                                                {
                                                                    (section.storeAt.length > 0) &&
                                                                    <>
                                                                        <p>
                                                                            {strings.storeAtHeader}
                                                                        </p>
                                                                    
                                                                        <div className='tags text-small'>
                                                                            {
                                                                                section.storeAt.map((label, i) =>
                                                                                    <div key={i} className='tag bg-white'>
                                                                                        <p>
                                                                                            {label}
                                                                                        </p>
                                                                                    </div>
                                                                                )
                                                                            }

                                                                        </div>
                                                                    </>
                                                                }

                                                                {
                                                                    (section.productAt.length > 0) &&
                                                                    <>
                                                                        <p>
                                                                            {strings.productAtHeader}
                                                                        </p>
                                                                    
                                                                        <div className='tags text-small'>

                                                                            {section.productAt.map((label, i) => (
                                                                                <div className='tag bg-white' key={i}>
                                                                                    <p>
                                                                                        {label}
                                                                                    </p>
                                                                                </div>
                                                                            ))}

                                                                        </div>
                                                                    </>
                                                                }

                                                            </div>
                                                        )
                                                    }

                                                </div>
                                            )}

                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    }

                </div>
            </Section>
            <Section
                bgColor='white'
                className='page-product__related-packaging'
                paddingTop='medium'
            >
                <div className='container'>

                    {
                        (additionalPackaging.inStore.packagingOptions.length > 0) &&
                        <div className='page-product__related-packaging__top'>

                            {
                                additionalPackaging.heading &&
                                <h2 className='top-title h3'>
                                    {additionalPackaging.heading}
                                </h2>
                            }

                            <div className='products-box'>

                                {
                                    additionalPackaging.inStore.heading &&
                                    <h3 className='products-box__title h4'>
                                        {additionalPackaging.inStore.heading}
                                    </h3>
                                }

                                <div className='products-box__grid'>
                                    {
                                        additionalPackaging.inStore.packagingOptions.map((opt, i) =>
                                            <ProductCardBarcode
                                                key={i}
                                                bgColor='white'
                                                title={opt.volume}
                                                quantity={opt.quantity}
                                                src={opt.image?.link}
                                                alt={opt.image?.alt}
                                                barcode={opt.barcode}
                                            />
                                        )
                                    }

                                </div>

                            </div>

                        </div>
                    }
                    {
                        (additionalPackaging.largeHouseholds.packagingOptions.length > 0) &&
                        <div className='page-product__related-packaging__bottom'>
                            <div className='products-box'>
                                <div className={clsx(
                                    'accordion',
                                    isProductsAccordionActive && 'is-active'
                                )}>

                                    <button
                                        type='button'
                                        className='accordion__title'
                                        onClick={toggleProductsAccordion}
                                    >

                                        <UxAngleDown />

                                        {
                                            additionalPackaging.largeHouseholds.heading &&
                                            <h3 className='products-box__title h4'>
                                                {additionalPackaging.largeHouseholds.heading}
                                            </h3>
                                        }

                                    </button>

                                    <div className='accordion__content'>
                                        <div>

                                            <div className='grid'>

                                                {
                                                    additionalPackaging.largeHouseholds.packagingOptions.map((opt, i) =>
                                                        <ProductCardBarcode
                                                            key={i}
                                                            bgColor='white'
                                                            title={opt.volume}
                                                            quantity={opt.quantity}
                                                            src={opt.image?.link}
                                                            alt={opt.image?.alt}
                                                            barcode={opt.barcode}
                                                        />
                                                    )
                                                }

                                            </div>
                                            {
                                                additionalPackaging.footnote &&
                                                <RichText
                                                    className='bottom-text'
                                                    content={additionalPackaging.footnote}
                                                />
                                            }

                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    }

                </div>
            </Section>

            <Section
                bgColor={colors.bgColor}
                className='page-product__reviews'
            >
                <div className='container'>

                    <div className='page-product__reviews__top'>
                        <div className='row'>
                            <div className='col-md-9 col-lg-7 col-xl-5'>

                                <h2 className={clsx(
                                    'title-top h3',
                                    colors.textColor
                                )}>
                                    {strings.reviewsHeader} {title}?
                                </h2>

                                <Fancybox
                                    options={{
                                        autoFocus: false,
                                        dragToClose: false,
                                        on: {
                                            close: () => {
                                                setFancyEventInit(true)
                                            },
                                            done: (_fancybox, slide) => {
                                                const _fancyboxParam = _fancybox
                                                function hideErrorMessage() {
                                                    const errorMessage = modal?.querySelector('.error-message')
                                                    if (errorMessage) {
                                                        errorMessage.remove()
                                                    }
                                                }
                                                function showErrorMessage(message) {
                                                    const errorMessage = modal?.querySelector('.error-message')

                                                    if (errorMessage) hideErrorMessage()

                                                    const newErrorMessage = document.createElement('p')
                                                    newErrorMessage.classList.add('error-message', 'red', 'text-smaller')
                                                    newErrorMessage.innerText = message
                                                    modal?.querySelector('.popup__content')?.appendChild(newErrorMessage)
                                                }
                                                function closeModal() {
                                                    const ratingInputs = modal?.querySelectorAll('input[name="rating"]') as NodeListOf<HTMLInputElement>

                                                    modal?.classList?.remove('is-sending')

                                                    ratingInputs.forEach(input => {
                                                        input.checked = false
                                                    })

                                                    _fancyboxParam.close()
                                                }

                                                const modal = document.getElementById('rating-modal')
                                                const confirmVote = modal?.querySelector('.confirm-vote')
                                                let selectedRating: any = null

                                                if (confirmVote && !fancyEventInit) {
                                                    confirmVote.addEventListener('click', function () {
                                                        const ratingInputs = modal?.querySelectorAll('input[name="rating"]') as NodeListOf<HTMLInputElement>

                                                        selectedRating = Array.from(ratingInputs).find(input => input.checked)

                                                        if (!selectedRating) {
                                                            showErrorMessage(strings.rateProductEmptyError)
                                                        } else {
                                                            modal?.classList?.add('is-sending')
                                                            fetch(rateProductUrl, {
                                                                method: 'post',
                                                                body: JSON.stringify({
                                                                    contentId: productId,
                                                                    stars: selectedRating?.value
                                                                })
                                                            })
                                                                .then(response => {
                                                                    if (response.ok) {
                                                                        return response.json()
                                                                    } else {
                                                                        throw Error('Error')
                                                                    }
                                                                })
                                                                // if success
                                                                .then((responseData) => {
                                                                    hideErrorMessage()
                                                                    setTotalReviewsState(responseData.totalRating)
                                                                    setRatingValue(responseData.rating)
                                                                    closeModal()
                                                                })
                                                                // if error
                                                                .catch(error => {
                                                                    modal?.classList?.remove('is-sending')
                                                                    showErrorMessage(strings.rateProductGeneralError)
                                                                })
                                                        }
                                                    })
                                                }
                                            }
                                        }
                                    }}
                                >

                                    <a
                                        href='#rating-modal'
                                        data-fancybox
                                        className={clsx('rating-button', colors.textColor)}
                                    >

                                        <Stars howMany={ratingValue || 0} isWhite={colors.textColor === 'white'} />

                                        <span>
                                            {totalReviewsState} {strings.reviewsCount}
                                        </span>

                                    </a>

                                    <div id='rating-modal' className='popup popup--small'>
                                        <div className='popup__wrapper'>
                                            <div className='popup__close'>
                                                <button
                                                    className='popup__close-button link-w-icon link-w-icon--icon-right text-smaller'
                                                    type='button'
                                                    data-fancybox-close
                                                >
                                                    <span>
                                                        {strings.modalClose}
                                                    </span>
                                                    <GeneralClose variant='outline' />
                                                </button>
                                            </div>

                                            <div className='popup__content'>

                                                <div className='rating-stars'>

                                                    {[5, 4, 3, 2, 1].map((rating) => (
                                                        <React.Fragment key={rating}>

                                                            <input
                                                                type='radio'
                                                                name='rating'
                                                                value={rating}
                                                                id={`rating-${rating}`}
                                                            />

                                                            <label htmlFor={`rating-${rating}`}>
                                                                <GeneralStar variant='white' />
                                                            </label>

                                                        </React.Fragment>
                                                    ))}

                                                </div>

                                                <Button
                                                    className='confirm-vote'
                                                    type='submit'
                                                    hasBorders
                                                    hover='blue'
                                                    noUnderline
                                                >
                                                    {strings.rateProductModalSubmit}
                                                </Button>

                                            </div>

                                        </div>
                                    </div>

                                </Fancybox>

                            </div>
                        </div>
                    </div>

                    <div className='page-product__reviews__grid'>
                        <MasonryGrid
                            items={reviews}
                            columnGutter={20}
                            maxColumnCount={2}
                            breakpointCols={{
                                default: 2,
                                767: 1
                            }}
                            columnWidth={325}
                            className='reviews-grid'
                            render={({ data }, i) => (
                                <div key={i} className='block bg-white'>

                                    <h3 className='h4 review-title'>
                                        {data.title}
                                    </h3>

                                    <div className='review-content'>
                                        <RichText content={data.content} />
                                    </div>

                                    <div className='review-bottom'>

                                        <EmojiHappy variant='outline' />

                                        <p className='text-smaller'>
                                            {data.name ? `${data.name}, ` : ''}{dayjs(data.date).format('D. MMM. YYYY')}
                                        </p>

                                    </div>

                                </div>
                            )}
                        />
                    </div>

                    <div className='page-product__reviews__bottom'>

                        {hasNextPage && (
                            <div className='load-more'>
                                <Link
                                    text={strings.loadMoreReviews}
                                    onClick={handleLoadMoreReviews}
                                    iconPosition='right'
                                    icon={<UxAngleDown />}
                                    className={colors.textColor}
                                />
                            </div>
                        )}

                        {isLogged ? (
                            <div className='row' style={{ width: '100%' }}>
                                <div className='col-sm-10 offset-sm-1 col-md-8 offset-md-2 col-lg-6 offset-lg-3'>
                                    <Form
                                        endpoint={commentProductUrl}
                                        className={clsx('reviews-form', colors.textColor)}
                                        onSuccess={() => {
                                            window.location.reload()
                                        }}
                                        popupErrorContent={{
                                            hasCloseButton: true,
                                            closeButtonText: strings.modalClose,
                                            title: strings.formPopupErrorTitle,
                                            content: strings.formPopupErrorContent,
                                            actionButtons: {
                                                primary: {
                                                    text: strings.modalClose,
                                                    closeOnClick: true,
                                                }
                                            }
                                        }}
                                    >

                                        <Input
                                            id='review-content-title'
                                            label={strings.inputTextLabel}
                                            name='review-content-title'
                                            type='text'
                                            required
                                            size='full-width'
                                            errorMessages={{
                                                required: strings.requiredError
                                            }}
                                        />

                                        <TextArea
                                            id='review-content-message'
                                            label={strings.textareaLabel}
                                            name='review-content-message'
                                            required
                                            size='full-width'
                                            minLength={5}
                                            maxLength={{
                                                limit: 200,
                                                text: strings.maxLengthMessage
                                            }}
                                            errorMessages={{
                                                required: strings.requiredError,
                                                maxLength: strings.maxLengthError,
                                                minLength: strings.minLengthError
                                            }}
                                        />

                                        <Input id='review-content-id' type='hidden' name='review-content-id' value={productId} />
                                        <Input id='review-date' type='hidden' name='review-date' value={dayjs().format('YYYY-MM-DDTHH:mm:ss')} />

                                        <Button
                                            type='submit'
                                            hover='black'
                                            hasBorders
                                            noUnderline
                                        >
                                            {strings.formPopupErrorSubmit}
                                        </Button>
                                    </Form>
                                </div>
                            </div>
                        ) : (
                            <Button
                                href={profilePageUrl}
                                hover='black'
                                hasBorders
                                noUnderline
                            >
                                {strings.loginButton}
                            </Button>
                        )}

                    </div>

                </div>
            </Section>
        </>
    )
}

export default (props) => <ProductLayout {...props} />
